import { ref, computed } from 'vue';
import { usePaymentRules } from './usePaymentRules';
import { useExpenseConciliation } from './useExpenseConciliation';
import { PERIOD_STATUS } from './usePayrollPeriods';
import { log, statusTrace } from '@/utils/DebugLogger';
import { eventBus } from '@/eventBus';
import { useI18n } from 'vue-i18n';

/**
 * Composable for payroll rules loading and type detection
 *
 * Purpose: Manages payment rules for the payroll wizard.
 *
 * Responsibilities:
 * - Loads payment rules filtered by franchise and frequency
 * - Provides computed properties for rule type detection (hasGrossRevenueRules, etc.)
 * - Extracts user IDs and payment rule IDs from rules
 * - Auto-completes expense conciliation when expenses step is skipped
 *
 * This is a focused composable extracted from usePayrollWizardData
 * to improve separation of concerns and testability.
 *
 * @returns {Object} Rules engine state and functions
 */
export function usePayrollRulesEngine() {
  const { t } = useI18n();
  const { listRulesByUserAndFranchise, listRulesByFranchiseAndFrequency, getRuleTypeLabel, getFrequencyLabel } = usePaymentRules();
  const { completeExpenseConciliation } = useExpenseConciliation();

  // =============================================================================
  // RULES STATE
  // =============================================================================

  /**
   * Payment rules preview - rules that will be executed for this period
   */
  const rulesPreview = ref([] as any);

  // =============================================================================
  // RULE TYPE DETECTION
  // =============================================================================

  /**
   * Check if there are gross revenue commission rules
   * Used to determine if revenue conciliation step should be shown
   */
  const hasGrossRevenueRules = computed(() => {
    return rulesPreview.value.some((rule: any) => rule.rule_type?.startsWith('commission_over_gross_revenue'));
  });

  /**
   * Check if there are commission_over_gross_revenue_executed rules
   * These are rules where operadores collect cash from customers and keep it.
   * Used to determine if advances step should be shown.
   */
  const hasCommissionOverExecutionRules = computed(() => {
    return rulesPreview.value.some((rule: any) => rule.rule_type === 'commission_over_gross_revenue_executed');
  });

  // =============================================================================
  // EXTRACTED IDS FROM RULES
  // =============================================================================

  /**
   * Extract unique user_ids from payment rules
   * Used by revenue conciliation to filter recebimentos
   */
  const ruleUserIds = computed(() => {
    const userIds = rulesPreview.value
      .filter((rule: any) => rule.user_id)
      .map((rule: any) => rule.user_id);
    const uniqueUserIds = [...new Set(userIds)];

    log('usePayrollRulesEngine', 'ruleUserIds', 'Computed user IDs from rules', {
      rulesCount: rulesPreview.value.length,
      rulesWithUserId: rulesPreview.value.filter((r: any) => r.user_id).length,
      userIds: uniqueUserIds
    });

    return uniqueUserIds;
  });

  /**
   * Extract payment rule IDs for the HYBRID ARCHITECTURE
   *
   * WHY paymentRuleIds (not executionIds)?
   * → The business constraint is at the RULE level, not execution level
   * → Junction table has UNIQUE(payment_rule_id, recebimento_id)
   * → Different executions of the SAME rule cannot claim the same recebimento
   */
  const rulePaymentRuleIds = computed(() => {
    const paymentRuleIds = rulesPreview.value
      .filter((rule: any) => rule.id)
      .map((rule: any) => Number(rule.id));
    const uniqueIds = [...new Set(paymentRuleIds)];

    log('usePayrollRulesEngine', 'rulePaymentRuleIds', 'Computed payment rule IDs', {
      rulesCount: rulesPreview.value.length,
      paymentRuleIds: uniqueIds
    });

    return uniqueIds;
  });

  // =============================================================================
  // LOADING FUNCTIONS
  // =============================================================================

  /**
   * Load payment rules preview for employee-centered architecture
   * Filters rules by user_id and franchise_id
   *
   * @param {number} franchiseId - Franchise ID
   * @param {string} userId - Employee Cognito sub
   * @returns {Promise<Array>} Array of payment rules for this employee
   */
  const loadRulesPreviewByUser = async (franchiseId: any, userId: any) => {
    try {
      log('usePayrollRulesEngine', 'loadRulesPreviewByUser', 'Loading rules', {
        franchiseId,
        userId
      });

      const rules = await listRulesByUserAndFranchise(franchiseId, userId);
      rulesPreview.value = rules;

      log('usePayrollRulesEngine', 'loadRulesPreviewByUser', 'Rules loaded', {
        count: rulesPreview.value.length
      });

      return rulesPreview.value;
    } catch (error) {
      console.error('Error loading rules by user:', error);
      throw error;
    }
  };

  /**
   * @deprecated Use loadRulesPreviewByUser instead (employee-centered architecture)
   * Load payment rules preview for the configured period
   *
   * @param {number} franchiseId - Franchise ID
   * @param {string} frequency - Payment frequency (deprecated)
   * @returns {Promise<Array>} Array of payment rules
   */
  const loadRulesPreview = async (franchiseId: any, frequency: any) => {
    try {
      log('usePayrollRulesEngine', 'loadRulesPreview', 'DEPRECATED - Loading rules by franchise only', {
        franchiseId,
        frequency
      });

      const rules = await listRulesByFranchiseAndFrequency(franchiseId, frequency);
      rulesPreview.value = rules;

      log('usePayrollRulesEngine', 'loadRulesPreview', 'Rules loaded', {
        count: rulesPreview.value.length
      });

      return rulesPreview.value;
    } catch (error) {
      console.error('Error loading rules:', error);
      throw error;
    }
  };

  /**
   * Auto-complete expense conciliation (always skipped now)
   *
   * The expenses step is no longer shown since profit withdrawal rules were removed.
   * This function always auto-completes to advance the backend status to EXPENSES (2).
   *
   * @param {Object} activePeriod - The active period object
   * @returns {Promise<Object|null>} Result of auto-complete or null if skipped
   */
  const autoCompleteExpensesIfNotNeeded = async (activePeriod: any) => {
    statusTrace('usePayrollRulesEngine.autoCompleteExpensesIfNotNeeded', 'FUNCTION_ENTRY', {
      periodId: activePeriod?.id,
      status: activePeriod?.status
    });

    // Only auto-complete if:
    // 1. We have a period ID
    // 2. Period status is not already >= EXPENSES
    if (!activePeriod?.id) {
      log('usePayrollRulesEngine', 'autoCompleteExpensesIfNotNeeded', 'No period ID, skipping');
      return null;
    }

    const currentStatus = parseInt(activePeriod.status);
    if (!isNaN(currentStatus) && currentStatus >= PERIOD_STATUS.EXPENSES) {
      log('usePayrollRulesEngine', 'autoCompleteExpensesIfNotNeeded', 'Period already past EXPENSES stage, skipping', {
        currentStatus,
        EXPENSES_THRESHOLD: PERIOD_STATUS.EXPENSES
      });
      return { alreadyCompleted: true };
    }

    log('usePayrollRulesEngine', 'autoCompleteExpensesIfNotNeeded', 'Auto-completing expenses', {
      periodId: activePeriod.id,
      currentStatus
    });

    try {
      const result = await completeExpenseConciliation(activePeriod.id);

      statusTrace('usePayrollRulesEngine.autoCompleteExpensesIfNotNeeded', 'AFTER_API_SUCCESS', {
        periodId: activePeriod.id,
        status: PERIOD_STATUS.EXPENSES,
        apiResult: result
      });

      log('usePayrollRulesEngine', 'autoCompleteExpensesIfNotNeeded', 'Expenses auto-completed', result);

      return {
        success: true,
        amount: result.amount || 0,
        count: result.count || 0,
        newStatus: PERIOD_STATUS.EXPENSES
      };
    } catch (error) {
      log('usePayrollRulesEngine', 'autoCompleteExpensesIfNotNeeded', 'CRITICAL: Failed to auto-complete expenses', error);
      console.error('Failed to auto-complete expense conciliation:', error);

      eventBus.emit('globalMessage.error', {
        message: t('Erro ao completar conciliação de despesas automaticamente. Por favor, tente novamente.'),
        duration: 5000
      });

      throw error;
    }
  };

  /**
   * Reset rules data
   */
  const resetRules = () => {
    rulesPreview.value = [];
  };

  return {
    // State
    rulesPreview,

    // Computed - Rule type detection
    hasGrossRevenueRules,
    hasCommissionOverExecutionRules,

    // Computed - Extracted IDs
    ruleUserIds,
    rulePaymentRuleIds,

    // Helper functions
    getRuleTypeLabel,
    getFrequencyLabel,

    // Functions
    loadRulesPreviewByUser,  // Employee-centered: use this for new code
    loadRulesPreview,        // Deprecated: for backward compatibility
    autoCompleteExpensesIfNotNeeded,
    resetRules
  };
}
